import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router';
import { Toast } from './components/Toast/Toast';
import { openStorage } from './db/idb';
import { useT } from './i18n';
import { applyColumnColours } from './lib/columns';
import { initNative } from './native/init';
import { startPushWatch } from './push/web';
import { router } from './routes';
import { useCards } from './store/cards';
import { useSession } from './store/session';
import { useEvents } from './store/events';
import { useSettings } from './store/settings';
import { openCardAnywhere } from './lib/openCard';

export function App() {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const db = await openStorage();
        await useCards.getState().hydrate(db);
        await useSettings.getState().hydrate(db);
        await useEvents.getState().hydrate(db);
        await useSession.getState().init();
        if (!cancelled) setReady(true);
        void initNative(router);
        if (useSession.getState().status === 'authed') void startPushWatch();
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = t.locale;
  }, [t.locale]);

  // A custom number of columns gets its colours from a hue sweep; the theme may flip underneath.
  const columnCount = useSettings((s) => s.columnCount);
  const theme = useSettings((s) => s.theme);
  useEffect(() => {
    applyColumnColours(columnCount);
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyColumnColours(columnCount);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [columnCount, theme]);

  // A later sign-in renews the browser's push subscription; a notification click opens its card.
  useEffect(() => {
    const unsubscribe = useSession.subscribe((s, prev) => {
      if (s.status === 'authed' && prev.status !== 'authed') void startPushWatch();
    });
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; cardId?: string | null } | null;
      if (data?.type !== 'open-card') return;
      void router.navigate('/');
      if (data.cardId) openCardAnywhere(data.cardId);
    };
    if ('serviceWorker' in navigator) navigator.serviceWorker.addEventListener('message', onMessage);
    return () => {
      unsubscribe();
      if ('serviceWorker' in navigator) navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, []);

  if (error) {
    return (
      <main style={{ padding: '2rem 1rem', maxWidth: '40rem', margin: '0 auto' }}>
        <h1 className="display">{t.t('app.name')}</h1>
        <p>{error}</p>
      </main>
    );
  }
  if (!ready) return null;
  // The toast lives above the router so every page shows it, Settings included.
  return (
    <>
      <RouterProvider router={router} />
      <Toast />
    </>
  );
}
